import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';

import PublicLogin from './pages/Public/PublicLogin';
import AdminRecovery from './pages/Public/AdminRecovery';
import OlvideContrasena from './pages/Public/OlvideContrasena';
import RestablecerContrasena from './pages/Public/RestablecerContrasena';
import LandingPage from './pages/Public/LandingPage';
import TenantLogin from './pages/Tenant/TenantLogin';
import DashboardLayout from './layouts/DashboardLayout';

// Panel plataforma (superadmin)
import PublicDashboard from './pages/Public/Dashboard';
import TenantsManager from './pages/Public/TenantsManager';
import PlansManager from './pages/Public/PlansManager';
import AdminManager from './pages/Public/AdminManager';
import GlobalSettings from './pages/Public/GlobalSettings';
import FacturacionPlataforma from './pages/Public/FacturacionPlataforma';

// Panel ferretería (tenant)
import TenantDashboard from './pages/Tenant/Dashboard';
import CategoriasManager from './pages/Tenant/CategoriasManager';
import ProductosManager from './pages/Tenant/ProductosManager';
import ClientesManager from './pages/Tenant/ClientesManager';
import Carrito from './pages/Tenant/Carrito';
import HistorialVentas from './pages/Tenant/HistorialVentas';
import ProveedoresManager from './pages/Tenant/ProveedoresManager';
import ComprasManager from './pages/Tenant/ComprasManager';
import TenantSettings from './pages/Tenant/TenantSettings';
import RegistroOperaciones from './pages/Tenant/RegistroOperaciones';
import DocumentoLegal from './pages/DocumentoLegal';
import UsuariosManager from './pages/Tenant/UsuariosManager';
import CierreCaja from './pages/Tenant/CierreCaja';
import Reportes from './pages/Tenant/Reportes';
import AjusteInventario from './pages/Tenant/AjusteInventario';

function RequireAuth({ children, loginPath, requireAdmin = false }) {
  // Rutas admin verifican su propia clave; rutas tenant verifican la suya.
  // Así una sesión no puede acceder al área de la otra.
  const token = requireAdmin
    ? localStorage.getItem('admin_access_token')
    : sessionStorage.getItem('access_token');
  if (!token) return <Navigate to={loginPath} replace />;
  return children;
}

const ROLE_DEFAULT = {
  Administrador: '/system',
  Supervisor: '/system',
  Cajero: '/system/carrito',
};

function RequireRole({ children, roles }) {
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const userRol = user.rol || (user.is_admin ? 'Administrador' : null);

  // Sin rol → no debería haber llegado aquí; redirigir al login
  if (!userRol) return <Navigate to="/login" replace />;

  if (roles && !roles.includes(userRol)) {
    return <Navigate to={ROLE_DEFAULT[userRol] || '/system'} replace />;
  }
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<TenantLogin />} />

          {/* Publicas: se leen antes de tener cuenta, y el enlace se puede
              compartir con un contador sin darle acceso al sistema. */}
          <Route path="/terminos" element={<DocumentoLegal clave="terminos" />} />
          <Route path="/privacidad" element={<DocumentoLegal clave="privacidad" />} />
          <Route path="/admin/login" element={<PublicLogin />} />
          <Route path="/admin/recuperar" element={<AdminRecovery />} />
          <Route path="/olvide-contrasena" element={<OlvideContrasena />} />
          <Route path="/restablecer" element={<RestablecerContrasena />} />

          <Route
            path="/system"
            element={
              <RequireAuth loginPath="/login">
                <DashboardLayout isPublic={false} />
              </RequireAuth>
            }
          >
            <Route index element={<RequireRole roles={['Administrador', 'Supervisor']}><TenantDashboard /></RequireRole>} />
            <Route path="categorias" element={<RequireRole roles={['Administrador', 'Supervisor']}><CategoriasManager /></RequireRole>} />
            <Route path="productos" element={<RequireRole roles={['Administrador', 'Supervisor']}><ProductosManager /></RequireRole>} />
            <Route path="clientes" element={<RequireRole roles={['Administrador', 'Supervisor', 'Cajero']}><ClientesManager /></RequireRole>} />
            <Route path="proveedores" element={<RequireRole roles={['Administrador', 'Supervisor']}><ProveedoresManager /></RequireRole>} />
            <Route path="compras" element={<RequireRole roles={['Administrador', 'Supervisor']}><ComprasManager /></RequireRole>} />
            <Route path="carrito" element={<Carrito />} />
            <Route path="caja" element={<CierreCaja />} />
            <Route path="reportes" element={<RequireRole roles={['Administrador', 'Supervisor']}><Reportes /></RequireRole>} />
            <Route path="historial" element={<RequireRole roles={['Administrador', 'Supervisor', 'Cajero']}><HistorialVentas /></RequireRole>} />
            <Route path="ajuste-inventario" element={<RequireRole roles={['Administrador', 'Supervisor']}><AjusteInventario /></RequireRole>} />
            <Route path="usuarios" element={<RequireRole roles={['Administrador']}><UsuariosManager /></RequireRole>} />
            <Route path="settings" element={<RequireRole roles={['Administrador']}><TenantSettings /></RequireRole>} />
            {/* Solo el Administrador: el registro dice que hizo cada empleado. */}
            <Route path="operaciones" element={<RequireRole roles={['Administrador']}><RegistroOperaciones /></RequireRole>} />
          </Route>

          <Route
            path="/admin"
            element={
              <RequireAuth loginPath="/admin/login" requireAdmin>
                <DashboardLayout isPublic={true} />
              </RequireAuth>
            }
          >
            <Route index element={<PublicDashboard />} />
            <Route path="planes" element={<PlansManager />} />
            <Route path="tenants" element={<TenantsManager />} />
            <Route path="pagos" element={<FacturacionPlataforma />} />
            <Route path="users" element={<AdminManager />} />
            <Route path="settings" element={<GlobalSettings />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
